// =============================================================================
// PREVIOUS IMPLEMENTATION — kept verbatim below for reference, intentionally
// inert. Line comments rather than a /* */ block: the original text contains
// comment terminators, which would close a block comment early.
//
// The active implementation follows after this block. Nothing above it is live
// code — it is here so the previous behaviour stays readable in place.
// =============================================================================

// import React, { FC } from "react";
// import StepFooter from "../StepFooter";
// import NoticeBar from "../NoticeBar";
// import { CheckCircleIcon, MailIcon, PhoneIcon } from "../icons";
// import { PanelProps } from "./types";
// import { RESERVATION } from "../reservation";
// import { useLocale } from "../../i18n/locale";
//
// // TODO: the support contact belongs to the DMS / Europea team, not the page.
// const SUPPORT = {
//   phone: "+33 1 84 06 00 00",
//   email: "concierge@europea-residences.com",
// };
//
// /** Step 7. The last step, so its footer carries no forward action. */
// const HomePanel: FC<PanelProps> = ({ onBack }) => {
//   const { t } = useLocale();
//
//   const summary = [
//     t.home.identityVerified,
//     t.home.contractSigned,
//     t.home.inventoryConfirmed,
//     t.home.insuranceReceived,
//   ];
//
//   return (
//     <>
//       <div className="relative">
//         <img
//           src={reservation.photo}
//           alt=""
//           className="h-56 w-full object-cover"
//         />
//       </div>
//
//       <section className="mt-8">
//         <h2 className="text-[14px] font-semibold text-navy-900">
//           {t.home.summary}
//         </h2>
//
//         <ul className="mt-4 grid grid-cols-2 gap-3">
//           {summary.map((item) => (
//             <li key={item} className="flex items-center gap-2.5">
//               <span className="flex-none text-green-600">
//                 <CheckCircleIcon className="h-4 w-4" />
//               </span>
//               <span className="text-[13px] text-neutral-700">{item}</span>
//             </li>
//           ))}
//         </ul>
//       </section>
//
//       <NoticeBar className="mt-7" body={t.home.documentsEmailed} />
//
//       {/* The support contact is hidden for every earlier step and revealed here
//           — onboarding is complete (spec, global business rules). */}
//       <section className="mt-7 bg-navy-800 p-6 text-white">
//         <h2 className="text-[14px] font-semibold">{t.home.needHelp}</h2>
//
//         <ul className="mt-4 flex flex-col gap-3">
//           <li>
//             <a
//               href={`tel:${SUPPORT.phone.replace(/\s/g, "")}`}
//               className="flex items-center gap-2.5 text-[13px] font-semibold text-white/90 transition-colors hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
//             >
//               <PhoneIcon className="h-4 w-4" />
//               {SUPPORT.phone}
//             </a>
//           </li>
//           <li>
//             <a
//               href={`mailto:${SUPPORT.email}`}
//               className="flex items-center gap-2.5 text-[13px] font-semibold text-white/90 transition-colors [overflow-wrap:anywhere] hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
//             >
//               <MailIcon className="h-4 w-4" />
//               {SUPPORT.email}
//             </a>
//           </li>
//         </ul>
//       </section>
//
//       <StepFooter onBack={onBack} />
//     </>
//   );
// };
//
// export default HomePanel;

// ==== ACTIVE IMPLEMENTATION ==================================================

import React, { FC, useState } from "react";
import ResultScreen from "../ResultScreen";
import { useReservation } from "../reservationContext";
import { useOnboardingRecord } from "../onboardingContext";
import { downloadTenantDocuments } from "../onboardingConfig";
import { PanelProps } from "./types";
import { useLocale } from "../../i18n/locale";
import { formatStayDate } from "../../i18n/formatDate";

/**
 * Step 7 — the last screen. The same result shape as the arrival summary, but
 * closing the whole journey rather than one half of it, so it recaps both
 * flows and hands the tenant on to their space.
 */
const HomePanel: FC<PanelProps> = ({ state, goTo }) => {
  const { locale, t } = useLocale();
  const reservation = useReservation();
  const onboarding = useOnboardingRecord();
  const c = t.desktop.homePanel;

  const [downloading, setDownloading] = useState(false);
  /** Names whatever the last attempt could not produce; empty when all is well. */
  const [downloadNote, setDownloadNote] = useState("");

  /**
   * Hand over the countersigned documents.
   *
   * The same fetch the arrival screen offers, repeated here because this is
   * where a tenant actually comes looking for their lease — the journey is
   * finished and this screen is what the link reopens on. It was wired with a
   * label and no handler, so the button rendered permanently disabled: the one
   * screen promising "Download my documents" was the one screen that could not.
   */
  // onboarding.contract_signed comes from the fetched record and is not updated
  // when the tenant signs in this same session — only the local state is. Using
  // both means the button enables straight after signing, without needing a
  // page refresh to refetch the record.
  const canDownload = !!onboarding?.contract_signed || state.documentsSigned;

  // As with contract_signed above, inventory.pdf_url comes from the fetched
  // record and is not filled in when the tenant signs the inventory in this same
  // session — markStepComplete only patches `steps`, so the URL stays "" until a
  // refresh refetches the record. Falling back to the local session flag means
  // the inventory report is included straight after signing, so both documents
  // download without needing a page refresh.
  const includeInventory =
    !!onboarding?.inventory.pdf_url || state.inventorySigned;

  const handleDownload = async () => {
    if (!onboarding) return;
    setDownloading(true);
    setDownloadNote("");
    // Contract plus the signed inventory report when the journey produced one
    // (a contract-only flow has no inventory report).
    const result = await downloadTenantDocuments(
      onboarding.generated_contract_pdf_id,
      onboarding.documents[0]?.title || "Rental contract",
      includeInventory
    );
    setDownloading(false);

    // Say so when something did not arrive. A tenant receiving one file out of
    // two and nothing explaining the difference is how this reached us at all:
    // the screen promised "my documents" and quietly handed over the inventory
    // report alone.
    const missing = [
      result.contract ? "" : c.missingContract,
      result.inventoryExpected && !result.inventory ? c.missingInventory : "",
    ].filter(Boolean);
    if (missing.length) setDownloadNote(c.downloadIncomplete(missing.join(", ")));
  };

  const items = [
    {
      id: "documents",
      title: c.documentsDone,
      caption: c.documentsDoneCaption,
    },
    {
      id: "inventory",
      title: c.inventoryDone,
      caption: c.inventoryDoneCaption(reservation.inspectionPhotoCount),
    },
    {
      id: "insurance",
      title: c.insuranceDone,
      // Naming the file is more reassuring than a generic confirmation.
      caption: state.insuranceFile?.name || c.insuranceDoneCaption,
    },
  ];

  return (
    <ResultScreen
      className="pt-8"
      eyebrow={c.eyebrow}
      title={c.title}
      subtitle={c.subtitle(
        reservation.tenantFirstName,
        reservation.property,
        formatStayDate(reservation.checkIn, locale)
      )}
      items={items}
      downloadLabel={c.download}
      onDownload={canDownload ? handleDownload : undefined}
      downloading={downloading}
      // Reused as the failure line: it sits directly under the actions, which
      // is exactly where a tenant looks after pressing Download.
      note={downloadNote || undefined}
      ctaLabel={c.cta}
      // No handler, so ResultScreen leaves the button out. The tenant space it
      // named does not exist yet, and the journey ends here in any case —
      // offering a way onward that goes nowhere is worse than offering none.
      // Pass a handler here once there is somewhere to send them.
      onContinue={undefined}
      restartLabel={c.restart}
      // Design-review only, as on the arrival screen: a tenant who has finished
      // has no reason to walk their own journey again.
      onRestart={onboarding ? undefined : () => goTo("inventory")}
    />
  );
};

export default HomePanel;
